import React from 'react';
import TutorialLayout from '../../components/TutorialLayout';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { getHostname } from '../../utils/getHostname';
import '../../components/DownloadProfileModal.css';
import './Mac.css';

function IOS() {
  const hostname = getHostname();
  useDocumentTitle('iOS VPN Setup');
  return (
    <TutorialLayout
      title="iOS guide"
      description={`This guide will walk you through downloading and installing the OpenVPN Connect app on your iPhone or iPad, and loading your VPN profile from ${hostname}.`}
    >
      <h4>1. Download Your VPN Profile</h4>
      <p>
        After a successful purchase, or when you open the link from your email, you will see a popup like the one below.
        Use it to download your VPN profile.
      </p>
      <ol>
        <li>Click the "Download VPN Profile" button in the popup to download your VPN profile zip file.</li>
        <li>Save the zip file to a location you can easily find, such as your Downloads folder.</li>
        <li>Extract the zip file to get the .ovpn file. You'll need this file to import into the OpenVPN Connect app.</li>
      </ol>
      <div className="post-images">
        <img src="/assets/images/tutorials/login.png" alt="Download VPN profile popup after purchase or email link" />
      </div>
      <p><strong>Note:</strong> Keep this .ovpn file safe as you'll need it to configure the OpenVPN Connect app.</p>

      <h4>2. Download and Install OpenVPN Connect</h4>
      <p>Now you need to download and install the OpenVPN Connect app for iOS:</p>
      <ol>
        <li>
          Open the App Store on your iPhone or iPad.
        </li>
        <li>
          Search for "OpenVPN Connect" or click the button below to open it directly:
          <div style={{ marginTop: '15px', marginBottom: '15px', maxWidth: '450px' }}>
            <a 
              href="https://apps.apple.com/us/app/openvpn-connect/id590379981" 
              className="download-vpn-button"
              target="_blank"
              rel="noopener noreferrer"
            >
              Download OpenVPN Connect for iOS
            </a>
          </div>
        </li>
        <li>Tap "Get" or the download button to download and install the OpenVPN Connect app on your device.</li>
        <li>Once installation is complete, tap "Open" to launch the app, or find the OpenVPN Connect icon on your home screen.</li>
      </ol>

      <h4>3. Import Your VPN Profile into OpenVPN Connect</h4>
      <p>Now that you have OpenVPN Connect installed and your VPN profile downloaded, it's time to import the .ovpn file:</p>
      <ol>
        <li>Open the OpenVPN Connect app on your iPhone or iPad.</li>
        <li>If this is your first time using the app, you'll see a welcome screen. Tap "Allow" if prompted to grant necessary permissions for VPN connections.</li>
        <li>Tap the "+" (plus) button at the bottom of the screen to add a new profile.</li>
        <li>You'll see import options. Tap the "File" tab to import from your device storage.</li>
        <li>Navigate to where you saved your .ovpn file. If you downloaded it directly on your device, it should be in the Files app under "Downloads" or "On My iPhone/iPad".</li>
        <li>Select your .ovpn file. The app will parse the profile and display the profile details.</li>
        <li>Tap "Add" to save the profile. Your VPN profile will now appear in the main screen of OpenVPN Connect.</li>
      </ol>
      <p><strong>Note:</strong> If you have a direct link to your .ovpn file, you can also import it using the "URL" option in OpenVPN Connect.</p>

      <h4>4. Connect to VPN Using OpenVPN Connect</h4>
      <p>Once your profile is imported, you can connect to the VPN:</p>
      <ol>
        <li>In the OpenVPN Connect app's main screen, you should see your imported VPN profile listed.</li>
        <li>Tap the toggle switch next to your profile name to initiate the connection.</li>
        <li>You'll see a system prompt asking for permission to add a VPN configuration. Tap "Allow" to grant permission.</li>
        <li>Enter your device passcode or use Face ID/Touch ID if prompted.</li>
        <li>Wait for the connection to establish. The app will show "Connecting..." status, then "Connected" when successful.</li>
        <li>When connected, you'll see a VPN icon in your status bar (at the top of your screen) indicating an active VPN connection.</li>
        <li>The profile in the app will show "Connected" status, and you may see connection details like your new IP address and connection duration.</li>
      </ol>

      <h4>5. Verify Your Connection</h4>
      <p>To verify that your VPN connection is working:</p>
      <ol>
        <li>Check the status in the OpenVPN Connect app - it should show "Connected".</li>
        <li>Look for the VPN icon in your status bar, which indicates an active VPN connection.</li>
        <li>You can visit a website like <a href="https://whatismyipaddress.com" target="_blank" rel="noopener noreferrer">whatismyipaddress.com</a> in Safari to verify your IP address has changed.</li>
        <li>Open the OpenVPN Connect app to see connection details, including your new IP address and connection duration.</li>
      </ol>

      <h4>6. Disconnect from VPN</h4>
      <p>To disconnect from the VPN:</p>
      <ol>
        <li>Open the OpenVPN Connect app and tap the toggle switch next to your profile, or</li>
        <li>Go to Settings → VPN and toggle off the VPN connection, or</li>
        <li>Tap the profile in the app and select "Disconnect".</li>
      </ol>

      <h4>7. Enjoy {hostname}</h4>
      <p>You're all set! Once connected, all your internet traffic will be routed through the {hostname} VPN server, providing you with secure and private browsing. You can connect and disconnect anytime using the OpenVPN Connect app.</p>
      
      <p><strong>Tips:</strong></p>
      <ul>
        <li>You can enable "Connect on Demand" in the OpenVPN Connect app settings to automatically connect when you're on untrusted networks.</li>
        <li>If you encounter any connection issues, make sure your .ovpn file is not corrupted.</li>
        <li>Keep your .ovpn file in a secure location in case you need to reimport it later or use it on another device.</li>
        <li>The OpenVPN Connect app supports auto-reconnect, so if your connection drops, it will attempt to reconnect automatically.</li>
        <li>On iOS, VPN connections are managed through the system Settings app as well, so you can check your VPN status there.</li>
      </ul>
    </TutorialLayout>
  );
}

export default IOS;
